import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { lazy, Suspense } from "react";
import CallButton from "./components/CallButton";
import CookieBanner from "./components/CookieBanner";
import Index from "./pages/Index";

// Lazy load all city and service pages to reduce initial bundle
const ZoneIntervention = lazy(() => import("./pages/ZoneIntervention"));
const CouvreurTreillieres = lazy(() => import("./pages/CouvreurTreillieres"));
const CouvreurNantes = lazy(() => import("./pages/CouvreurNantes"));
const CouvreurSaintHerblain = lazy(() => import("./pages/CouvreurSaintHerblain"));
const CouvreurReze = lazy(() => import("./pages/CouvreurReze"));
const CouvreurSaintNazaire = lazy(() => import("./pages/CouvreurSaintNazaire"));
const CouvreurCarquefou = lazy(() => import("./pages/CouvreurCarquefou"));
const CouvreurOrvault = lazy(() => import("./pages/CouvreurOrvault"));
const CouvreurCoueron = lazy(() => import("./pages/CouvreurCoueron"));
const CouvreurVertou = lazy(() => import("./pages/CouvreurVertou"));
const CouvreurBouguenais = lazy(() => import("./pages/CouvreurBouguenais"));
const CouvreurLaChapelleSurErdre = lazy(() => import("./pages/CouvreurLaChapelleSurErdre"));
const CouvreurSainteLuceSurLoire = lazy(() => import("./pages/CouvreurSainteLuceSurLoire"));
const CouvreurSautron = lazy(() => import("./pages/CouvreurSautron"));
const CouvreurLesSorinieres = lazy(() => import("./pages/CouvreurLesSorinieres"));
const CouvreurBasseGoulaine = lazy(() => import("./pages/CouvreurBasseGoulaine"));
const CouvreurHauteGoulaine = lazy(() => import("./pages/CouvreurHauteGoulaine"));
const CouvreurSaintSebastienSurLoire = lazy(() => import("./pages/CouvreurSaintSebastienSurLoire"));
const CouvreurPornic = lazy(() => import("./pages/CouvreurPornic"));
const CouvreurVallet = lazy(() => import("./pages/CouvreurVallet"));
const CouvreurClisson = lazy(() => import("./pages/CouvreurClisson"));
const CouvreurAncenis = lazy(() => import("./pages/CouvreurAncenis"));
const CouvreurGuerande = lazy(() => import("./pages/CouvreurGuerande"));
const CouvreurLaBaule = lazy(() => import("./pages/CouvreurLaBaule"));
const CouvreurSuceSurErdre = lazy(() => import("./pages/CouvreurSuceSurErdre"));
const CouvreurHeric = lazy(() => import("./pages/CouvreurHeric"));
const CouvreurVigneuxDeBretagne = lazy(() => import("./pages/CouvreurVigneuxDeBretagne"));
const CouvreurGrandchampDesFontaines = lazy(() => import("./pages/CouvreurGrandchampDesFontaines"));
const CouvreurNortSurErdre = lazy(() => import("./pages/CouvreurNortSurErdre"));
const CouvreurBlain = lazy(() => import("./pages/CouvreurBlain"));
const CouvreurChateaubriant = lazy(() => import("./pages/CouvreurChateaubriant"));
const CouvreurLaRocheSurYon = lazy(() => import("./pages/CouvreurLaRocheSurYon"));
const CouvreurLesSablesDOlonne = lazy(() => import("./pages/CouvreurLesSablesDOlonne"));
const CouvreurChallans = lazy(() => import("./pages/CouvreurChallans"));
const CouvreurFontenayLeComte = lazy(() => import("./pages/CouvreurFontenayLeComte"));
const CouvreurLucon = lazy(() => import("./pages/CouvreurLucon"));
const CouvreurLesHerbiers = lazy(() => import("./pages/CouvreurLesHerbiers"));
const CouvreurAizenay = lazy(() => import("./pages/CouvreurAizenay"));
const CouvreurSaintGillesCroixDeVie = lazy(() => import("./pages/CouvreurSaintGillesCroixDeVie"));
const CouvreurMontaigu = lazy(() => import("./pages/CouvreurMontaigu"));
const CouvreurChantonnay = lazy(() => import("./pages/CouvreurChantonnay"));
const CouvreurAngers = lazy(() => import("./pages/CouvreurAngers"));
const CouvreurCholet = lazy(() => import("./pages/CouvreurCholet"));
const CouvreurSaumur = lazy(() => import("./pages/CouvreurSaumur"));
const CouvreurAvrille = lazy(() => import("./pages/CouvreurAvrille"));
const CouvreurTrelaze = lazy(() => import("./pages/CouvreurTrelaze"));
const CouvreurLesPontsDeCe = lazy(() => import("./pages/CouvreurLesPontsDeCe"));
const CouvreurSaintBarthelemyDAnjou = lazy(() => import("./pages/CouvreurSaintBarthelemyDAnjou"));
const CouvreurBeaucouze = lazy(() => import("./pages/CouvreurBeaucouze"));
const CouvreurChemilleEnAnjou = lazy(() => import("./pages/CouvreurChemilleEnAnjou"));
const CouvreurBeaupreauEnMauges = lazy(() => import("./pages/CouvreurBeaupreauEnMauges"));
const Contact = lazy(() => import("./pages/Contact"));
const MentionsLegales = lazy(() => import("./pages/MentionsLegales"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Couverture = lazy(() => import("./pages/services/Couverture"));
const Zinguerie = lazy(() => import("./pages/services/Zinguerie"));
const PeintureHydrofuge = lazy(() => import("./pages/services/PeintureHydrofuge"));
const FacadeExterieur = lazy(() => import("./pages/services/FacadeExterieur"));
const DemoussageToiture = lazy(() => import("./pages/services/DemoussageToiture"));
const HydrofugeToiture = lazy(() => import("./pages/services/HydrofugeToiture"));
const RemplacementToiture = lazy(() => import("./pages/services/RemplacementToiture"));
const RenovationToiture = lazy(() => import("./pages/services/RenovationToiture"));
const TuileArdoise = lazy(() => import("./pages/services/TuileArdoise"));
const PoseVelux = lazy(() => import("./pages/services/PoseVelux"));
const TraitementCharpente = lazy(() => import("./pages/services/TraitementCharpente"));
const IsolationToiture = lazy(() => import("./pages/services/IsolationToiture"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <CallButton />
        <CookieBanner />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/zone-intervention" element={<ZoneIntervention />} />
            <Route path="/couvreur-treillieres" element={<CouvreurTreillieres />} />
            <Route path="/couvreur-nantes" element={<CouvreurNantes />} />
            <Route path="/couvreur-saint-herblain" element={<CouvreurSaintHerblain />} />
            <Route path="/couvreur-reze" element={<CouvreurReze />} />
            <Route path="/couvreur-saint-nazaire" element={<CouvreurSaintNazaire />} />
            <Route path="/couvreur-carquefou" element={<CouvreurCarquefou />} />
            <Route path="/couvreur-orvault" element={<CouvreurOrvault />} />
            <Route path="/couvreur-coueron" element={<CouvreurCoueron />} />
            <Route path="/couvreur-vertou" element={<CouvreurVertou />} />
            <Route path="/couvreur-bouguenais" element={<CouvreurBouguenais />} />
            <Route path="/couvreur-la-chapelle-sur-erdre" element={<CouvreurLaChapelleSurErdre />} />
            <Route path="/couvreur-sainte-luce-sur-loire" element={<CouvreurSainteLuceSurLoire />} />
            <Route path="/couvreur-sautron" element={<CouvreurSautron />} />
            <Route path="/couvreur-les-sorinieres" element={<CouvreurLesSorinieres />} />
            <Route path="/couvreur-basse-goulaine" element={<CouvreurBasseGoulaine />} />
            <Route path="/couvreur-haute-goulaine" element={<CouvreurHauteGoulaine />} />
            <Route path="/couvreur-saint-sebastien-sur-loire" element={<CouvreurSaintSebastienSurLoire />} />
            <Route path="/couvreur-pornic" element={<CouvreurPornic />} />
            <Route path="/couvreur-vallet" element={<CouvreurVallet />} />
            <Route path="/couvreur-clisson" element={<CouvreurClisson />} />
            <Route path="/couvreur-ancenis" element={<CouvreurAncenis />} />
            <Route path="/couvreur-guerande" element={<CouvreurGuerande />} />
            <Route path="/couvreur-la-baule" element={<CouvreurLaBaule />} />
            <Route path="/couvreur-suce-sur-erdre" element={<CouvreurSuceSurErdre />} />
            <Route path="/couvreur-heric" element={<CouvreurHeric />} />
            <Route path="/couvreur-vigneux-de-bretagne" element={<CouvreurVigneuxDeBretagne />} />
            <Route path="/couvreur-grandchamp-des-fontaines" element={<CouvreurGrandchampDesFontaines />} />
            <Route path="/couvreur-nort-sur-erdre" element={<CouvreurNortSurErdre />} />
            <Route path="/couvreur-blain" element={<CouvreurBlain />} />
            <Route path="/couvreur-chateaubriant" element={<CouvreurChateaubriant />} />
            <Route path="/couvreur-la-roche-sur-yon" element={<CouvreurLaRocheSurYon />} />
            <Route path="/couvreur-les-sables-d-olonne" element={<CouvreurLesSablesDOlonne />} />
            <Route path="/couvreur-challans" element={<CouvreurChallans />} />
            <Route path="/couvreur-fontenay-le-comte" element={<CouvreurFontenayLeComte />} />
            <Route path="/couvreur-lucon" element={<CouvreurLucon />} />
            <Route path="/couvreur-les-herbiers" element={<CouvreurLesHerbiers />} />
            <Route path="/couvreur-aizenay" element={<CouvreurAizenay />} />
            <Route path="/couvreur-saint-gilles-croix-de-vie" element={<CouvreurSaintGillesCroixDeVie />} />
            <Route path="/couvreur-montaigu" element={<CouvreurMontaigu />} />
            <Route path="/couvreur-chantonnay" element={<CouvreurChantonnay />} />
            <Route path="/couvreur-angers" element={<CouvreurAngers />} />
            <Route path="/couvreur-cholet" element={<CouvreurCholet />} />
            <Route path="/couvreur-saumur" element={<CouvreurSaumur />} />
            <Route path="/couvreur-avrille" element={<CouvreurAvrille />} />
            <Route path="/couvreur-trelaze" element={<CouvreurTrelaze />} />
            <Route path="/couvreur-les-ponts-de-ce" element={<CouvreurLesPontsDeCe />} />
            <Route path="/couvreur-saint-barthelemy-d-anjou" element={<CouvreurSaintBarthelemyDAnjou />} />
            <Route path="/couvreur-beaucouze" element={<CouvreurBeaucouze />} />
            <Route path="/couvreur-chemille-en-anjou" element={<CouvreurChemilleEnAnjou />} />
            <Route path="/couvreur-beaupreau-en-mauges" element={<CouvreurBeaupreauEnMauges />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/services/couverture" element={<Couverture />} />
            <Route path="/services/zinguerie" element={<Zinguerie />} />
            <Route path="/services/peinture-hydrofuge" element={<PeintureHydrofuge />} />
            <Route path="/services/facade-exterieur" element={<FacadeExterieur />} />
            <Route path="/services/demoussage-toiture" element={<DemoussageToiture />} />
            <Route path="/services/hydrofuge-toiture" element={<HydrofugeToiture />} />
            <Route path="/services/remplacement-toiture" element={<RemplacementToiture />} />
            <Route path="/services/renovation-toiture" element={<RenovationToiture />} />
            <Route path="/services/tuile-ardoise" element={<TuileArdoise />} />
            <Route path="/services/pose-velux" element={<PoseVelux />} />
            <Route path="/services/traitement-charpente" element={<TraitementCharpente />} />
            <Route path="/services/isolation-toiture" element={<IsolationToiture />} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
